import React, { useState } from "react";
import { X, Send, Eye, ArrowLeft, Clock } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import StoryMediaUpload from "@/components/StoryMediaUpload";

const DURATION_CHIPS = [
  { hours: 24, label: "1 día" },
  { hours: 72, label: "3 días" },
  { hours: 168, label: "1 semana" },
  { hours: 336, label: "2 semanas" },
];

const chipLabel = (h) => DURATION_CHIPS.find((c) => c.hours === h)?.label || `${h} h`;

/**
 * Two-step publish modal: edit -> preview.
 *
 * Props:
 *   profileId, profileName
 *   onClose()
 *   onPublished(story)
 */
const StoryUploadModal = ({ profileId, profileName, onClose, onPublished }) => {
  const [step, setStep] = useState("edit"); // "edit" | "preview"
  const [media, setMedia] = useState(null); // { url, media_type }
  const [caption, setCaption] = useState("");
  const [hours, setHours] = useState(24);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const goPreview = (e) => {
    e.preventDefault();
    if (!media?.url) {
      setError("Sube una foto o video primero.");
      return;
    }
    setError("");
    setStep("preview");
  };

  const publish = async () => {
    setSaving(true);
    setError("");
    try {
      const { data } = await api.post("/stories", {
        profile_id: profileId,
        image_url: media.url,
        media_type: media.media_type,
        caption: caption.trim(),
        duration_hours: hours,
      });
      onPublished?.(data);
      onClose();
    } catch (err) {
      setError(formatApiError(err));
      setStep("edit");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="story-upload-modal"
    >
      <div
        className="bg-white rounded-3xl w-full max-w-md shadow-2xl overflow-hidden flex flex-col max-h-[95vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100">
          {step === "preview" && (
            <button
              type="button"
              onClick={() => setStep("edit")}
              className="w-9 h-9 rounded-full bg-cream hover:bg-orange hover:text-white text-teal-deep flex items-center justify-center"
              aria-label="Volver"
              data-testid="story-preview-back"
            >
              <ArrowLeft size={16} />
            </button>
          )}
          <div className="flex-1 min-w-0">
            <p className="eyebrow text-orange">
              {step === "edit" ? "Nueva historia" : "Vista previa"}
            </p>
            <h3 className="font-display text-xl text-teal-deep truncate">{profileName}</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-cream hover:bg-orange hover:text-white text-teal-deep flex items-center justify-center"
            aria-label="Cerrar"
          >
            <X size={16} />
          </button>
        </div>

        {step === "edit" ? (
          <form onSubmit={goPreview} className="flex flex-col flex-1 min-h-0">
            <div className="p-5 space-y-5 overflow-y-auto">
              <StoryMediaUpload value={media} onChange={setMedia} testid="story-media" />

              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-teal-soft block mb-1">
                  Leyenda (opcional) · <span className="text-gray-400 font-normal">{caption.length}/280</span>
                </label>
                <textarea
                  rows={3}
                  maxLength={280}
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  placeholder="ej. 20% OFF esta semana en todos los cortes ✂️"
                  className="field-input"
                  data-testid="story-caption-input"
                />
              </div>

              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-teal-soft block mb-2">
                  ¿Cuánto tiempo la muestro?
                </label>
                <div className="flex flex-wrap gap-2">
                  {DURATION_CHIPS.map((c) => (
                    <button
                      key={c.hours}
                      type="button"
                      onClick={() => setHours(c.hours)}
                      className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-all ${
                        hours === c.hours
                          ? "bg-orange text-white shadow"
                          : "bg-cream text-teal-soft hover:text-orange"
                      }`}
                      data-testid={`story-duration-${c.hours}`}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>

              {error && (
                <p className="text-sm text-red-600" data-testid="story-error">
                  {error}
                </p>
              )}
            </div>

            <div className="border-t border-gray-100 p-4">
              <button
                type="submit"
                disabled={!media?.url}
                className="btn-orange w-full justify-center disabled:opacity-50"
                data-testid="story-preview-cta"
              >
                <Eye size={14} /> Ver vista previa
              </button>
            </div>
          </form>
        ) : (
          <div className="flex flex-col flex-1 min-h-0">
            {/* Fullscreen-style preview — scrollable so CTA row is always visible on small screens */}
            <div className="flex-1 min-h-0 overflow-y-auto">
              <div
                className="relative bg-black flex items-center justify-center mx-auto"
                style={{ aspectRatio: "9 / 16", maxHeight: "min(65vh, 640px)" }}
              >
                {/* Fake progress bar */}
                <div className="absolute top-2 left-2 right-2 h-1 bg-white/25 rounded-full overflow-hidden">
                  <div className="h-full w-1/3 bg-white rounded-full" />
                </div>
                {media?.media_type === "video" ? (
                  <video
                    src={media.url}
                    className="max-w-full max-h-full object-contain"
                    autoPlay
                    muted
                    playsInline
                    loop
                    data-testid="story-preview-video"
                  />
                ) : (
                  <img
                    src={media?.url}
                    alt=""
                    className="max-w-full max-h-full object-contain"
                    data-testid="story-preview-image"
                  />
                )}
                {caption && (
                  <div className="absolute bottom-4 left-3 right-3 z-10 bg-black/55 backdrop-blur-sm text-white text-sm rounded-2xl px-4 py-3 leading-snug" data-testid="story-preview-caption">
                    {caption}
                  </div>
                )}
              </div>

              <div className="px-5 py-3 flex items-center justify-between text-xs text-teal-soft">
                <span className="inline-flex items-center gap-1">
                  <Clock size={12} /> Visible por {chipLabel(hours)}
                </span>
                <span className="uppercase tracking-wider font-bold text-orange">
                  {media?.media_type === "video" ? "Video" : "Foto"}
                </span>
              </div>

              {error && (
                <p className="px-5 pb-3 text-sm text-red-600" data-testid="story-error">
                  {error}
                </p>
              )}
            </div>

            <div className="border-t border-gray-100 p-4 grid grid-cols-2 gap-2 shrink-0">
              <button
                type="button"
                onClick={() => setStep("edit")}
                className="btn-teal-outline justify-center"
                data-testid="story-preview-edit"
              >
                Volver a editar
              </button>
              <button
                type="button"
                onClick={publish}
                disabled={saving}
                className="btn-orange justify-center disabled:opacity-50"
                data-testid="story-publish"
              >
                <Send size={14} /> {saving ? "Publicando…" : "Publicar"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StoryUploadModal;
